import React, { useRef, useState } from "react";
import { get, newKey, outcomeUnknown, post } from "../api";
import { errText, fmtNum, fmtSar, getLang, nameOf, t } from "../i18n";
import { refreshWallet, toast, useStore } from "../store";
import { Button, Coins, Empty, GiftIcon, Link, Modal, Spinner, cx, useAsync } from "../ui";

const COSMETIC = ["frame", "avatar", "table_skin", "effect"];

export function Store() {
  const pk = useAsync(() => get("/api/store/packages"));
  const gifts = useAsync(() => get("/api/store/gifts"));
  const items = useAsync(() => get("/api/me/items"));
  const balance = useStore((s) => s.balance);
  const [buying, setBuying] = useState<any>(null);
  const [cosmetic, setCosmetic] = useState<any>(null);
  const owned = new Set((items.data?.items ?? []).map((i: any) => i.id));
  const all = gifts.data?.gifts ?? [];
  const byCat = (cats: string[]) => all.filter((g: any) => cats.includes(g.category));

  return (
    <div className="store">
      <header className="store-head">
        <h1>{t.store}</h1>
        <span className="store-balance">{t.balance}: <Coins n={balance?.total ?? 0} /></span>
      </header>
      <section>
        <h2>{t.buyCoins}</h2>
        {pk.loading && !pk.data ? (
          <Spinner />
        ) : (
          <div className="packages">
            {(pk.data?.packages ?? []).map((p: any) => (
              <button key={p.id} className="package" onClick={() => setBuying(p)}>
                {(getLang() === "ar" ? p.promo_label_ar : p.promo_label_en) && <span className="tag">{getLang() === "ar" ? p.promo_label_ar : p.promo_label_en}</span>}
                <span className="package-coins"><Coins n={p.coins} /></span>
                {p.bonus_coins > 0 && <span className="package-bonus">{t.bonus(p.bonus_coins)}</span>}
                <span className="package-name">{nameOf(p)}</span>
                <strong className="package-price">{fmtSar(p.price_halalas, p.currency)}</strong>
              </button>
            ))}
          </div>
        )}
        <p className="muted small">{t.priceIncludesVat}</p>
      </section>
      <section>
        <h2>{t.giftShop}</h2>
        <div className="gift-grid">
          {byCat(["basic", "premium", "rare", "seasonal", "animated"]).map((g: any) => (
            <div key={g.id} className={cx("gift-card", `cat-${g.category}`)}>
              <GiftIcon icon={g.icon} />
              <span>{nameOf(g)}</span>
              <small className="muted">{t.categories[g.category]}</small>
              <Coins n={g.price_coins} />
            </div>
          ))}
        </div>
        <p className="muted small">{t.sendGift}: {t.friends} / {t.profile}</p>
      </section>
      <section>
        <h2>{t.cosmetics}</h2>
        <div className="gift-grid">
          {byCat(COSMETIC).map((g: any) => (
            <button key={g.id} className={cx("gift-card", `cat-${g.category}`, owned.has(g.id) && "is-owned")} onClick={() => !owned.has(g.id) && setCosmetic(g)}>
              <GiftIcon icon={g.icon} />
              <span>{nameOf(g)}</span>
              <small className="muted">{t.categories[g.category]}</small>
              {owned.has(g.id) ? <span className="tag-owned">{t.owned}</span> : <Coins n={g.price_coins} />}
            </button>
          ))}
        </div>
      </section>
      {buying && <Checkout pkg={buying} sandbox={pk.data?.paymentProvider === "sandbox"} onClose={() => setBuying(null)} />}
      {cosmetic && <BuyCosmetic item={cosmetic} onClose={() => { setCosmetic(null); items.reload(); }} />}
    </div>
  );
}

/**
 * One purchase attempt. Every action runs alone (a second tap while one is in flight is ignored), and any action whose
 * outcome is unknown (timeout, dropped connection) asks the server for the real status before allowing another try.
 * The idempotency key is created once per attempt, so re-sending the same request never charges twice.
 */
function Checkout({ pkg, sandbox, onClose }: { pkg: any; sandbox: boolean; onClose: () => void }) {
  const key = useRef(newKey());
  const inFlight = useRef(false);
  const [payment, setPayment] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<"paid" | "failed" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem(null);
    try {
      await action();
    } catch (e) {
      setProblem(errText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  /** Asks the server for the payment's real state. Returns null when that check itself failed. */
  const checkStatus = async (paymentId: string): Promise<string | null> => {
    try {
      const r = await get(`/api/store/payments/${paymentId}`, { timeoutMs: 10_000 });
      const st: string = r.payment.status;
      if (st === "paid") {
        setResult("paid");
        await refreshWallet();
      } else if (st === "failed" || st === "cancelled") {
        setResult("failed");
      } else {
        setPending(true);
      }
      return st;
    } catch {
      return null;
    }
  };

  const start = () =>
    run(async () => {
      const r = await post("/api/store/checkout", { packageId: pkg.id, idempotencyKey: key.current }, { timeoutMs: 15_000 });
      setPayment(r.payment);
    });

  const confirm = (outcome: "paid" | "failed") =>
    run(async () => {
      try {
        const r = await post("/api/store/sandbox/confirm", { paymentId: payment.id, outcome }, { timeoutMs: 15_000 });
        setResult(r.payment.status === "paid" ? "paid" : "failed");
        await refreshWallet();
      } catch (e) {
        if (!outcomeUnknown(e)) throw e;
        // The request may have reached the server: learn the outcome instead of guessing it.
        const st = await checkStatus(payment.id);
        if (st === null) setProblem(`${errText(e)} ${t.purchaseUnknown}`);
        else if (st === "pending") setProblem(`${errText(e)} ${t.purchasePending}`);
      }
    });

  const recheck = () =>
    run(async () => {
      const st = await checkStatus(payment.id);
      if (st === null) setProblem(t.purchaseUnknown);
      else if (st === "pending") setProblem(t.purchasePending);
    });

  return (
    <Modal title={t.confirmPurchase} onClose={onClose}>
      {result ? (
        <div className="stack center">
          <p className={result === "paid" ? "notice" : "form-error"}>{result === "paid" ? t.paymentDone : t.paymentFailed}</p>
          <Button onClick={onClose}>{t.close}</Button>
        </div>
      ) : (
        <div className="stack">
          <dl className="summary">
            <dt>{t.youGet}</dt>
            <dd><Coins n={pkg.coins} /> {pkg.bonus_coins > 0 && <small>{t.bonus(pkg.bonus_coins)}</small>}</dd>
            <dt>{t.youPay}</dt>
            <dd><strong>{fmtSar(pkg.price_halalas, pkg.currency)}</strong> <small className="muted">{t.priceIncludesVat}</small></dd>
          </dl>
          {sandbox && <p className="sandbox">{t.sandboxNotice}</p>}
          {problem && <p className="form-error" role="alert">{problem}</p>}
          {!sandbox ? (
            <p className="form-error" role="status">{t.paymentUnavailable}</p>
          ) : !payment ? (
            <Button busy={busy} onClick={start}>{t.payNow} {fmtSar(pkg.price_halalas, pkg.currency)}</Button>
          ) : (
            <div className="row">
              <Button busy={busy} onClick={() => confirm("paid")}>{t.simulatePaid}</Button>
              <Button kind="ghost" busy={busy} onClick={() => confirm("failed")}>{t.simulateFail}</Button>
              {pending && <Button kind="quiet" busy={busy} onClick={recheck}>{t.checkStatus}</Button>}
            </div>
          )}
          {busy && <p className="muted small" role="status">{t.checkingStatus}</p>}
        </div>
      )}
    </Modal>
  );
}

function BuyCosmetic({ item, onClose }: { item: any; onClose: () => void }) {
  const key = useRef(newKey());
  const inFlight = useRef(false);
  const balance = useStore((s) => s.balance);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const enough = (balance?.total ?? 0) >= item.price_coins;

  const buy = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem(null);
    try {
      await post("/api/store/items/buy", { giftId: item.id, expectedPrice: item.price_coins, idempotencyKey: key.current }, { timeoutMs: 15_000 });
      await refreshWallet();
      toast(t.owned);
      onClose();
    } catch (e) {
      if (outcomeUnknown(e)) {
        // The item may have arrived even though the response did not: check before offering another try.
        const owned = await get("/api/me/items", { timeoutMs: 10_000 })
          .then((r) => (r.items ?? []).some((i: any) => i.id === item.id))
          .catch(() => false);
        if (owned) {
          await refreshWallet();
          toast(t.owned);
          onClose();
          return;
        }
      }
      setProblem(errText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal title={nameOf(item)} onClose={onClose}>
      <div className="stack center">
        <GiftIcon icon={item.icon} size={96} />
        <p>{t.costs(item.price_coins)}</p>
        {problem && <p className="form-error" role="alert">{problem}</p>}
        {enough ? (
          <Button busy={busy} onClick={buy}>
            {t.buy} — {item.price_coins} {t.coins}
          </Button>
        ) : (
          <p className="form-error">{t.insufficient}</p>
        )}
        {busy && <p className="muted small" role="status">{t.checkingStatus}</p>}
      </div>
    </Modal>
  );
}

export function Wallet() {
  const w = useAsync(() => get("/api/wallet"));
  if (w.loading && !w.data) return <Spinner />;
  if (w.error) return <Empty>{errText(w.error)}</Empty>;
  const { balance, history, payments, gifts } = w.data;
  const date = (d: string) => new Date(d).toLocaleString(getLang() === "ar" ? "ar-SA" : "en");
  return (
    <div className="wallet">
      <header className="wallet-head">
        <div>
          <h1>{t.wallet}</h1>
          <p className="wallet-total"><Coins n={balance.total} /></p>
          <small className="muted">{t.paidCoins}: {fmtNum(balance.paid)} · {t.freeCoins}: {fmtNum(balance.free)}</small>
        </div>
        <Link to="/store" className="btn btn-primary">{t.topUp}</Link>
      </header>
      <section className="panel">
        <h2>{t.history}</h2>
        {history.length ? (
          <table className="ledger">
            <tbody>
              {history.map((h: any) => {
                const d = h.paid_delta + h.free_delta;
                return (
                  <tr key={h.id}>
                    <td>{t.ledgerKinds[h.kind] ?? h.kind}</td>
                    <td className={d >= 0 ? "pos" : "neg"}><bdi>{d >= 0 ? "+" : ""}{fmtNum(d)}</bdi></td>
                    <td className="muted">{date(h.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty>—</Empty>
        )}
      </section>
      <section className="panel">
        <h2>{t.giftsReceived} / {t.sendGift}</h2>
        {gifts.length ? (
          <ul className="list">
            {gifts.map((g: any) => (
              <li key={g.id} className="list-row">
                <GiftIcon icon={g.icon} size={32} />
                <span className="grow">{nameOf(g)} — {g.direction === "sent" ? `→ ${g.recipient}` : `← ${g.sender}`}</span>
                <Coins n={g.price_coins} />
              </li>
            ))}
          </ul>
        ) : (
          <Empty>—</Empty>
        )}
      </section>
      <section className="panel">
        <h2>{t.buyCoins}</h2>
        {payments.length ? (
          <table className="ledger">
            <tbody>
              {payments.map((p: any) => (
                <tr key={p.id}>
                  <td>{fmtSar(p.amount_halalas, p.currency)}</td>
                  <td><Coins n={p.coins} /></td>
                  <td>{p.status}</td>
                  <td className="muted">{date(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>—</Empty>
        )}
      </section>
    </div>
  );
}
